import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/zh-cn";
import "dayjs/locale/zh-tw";
import "dayjs/locale/vi";
import "dayjs/locale/es";
import "dayjs/locale/th";
import "dayjs/locale/id";
import "dayjs/locale/pt";
import "dayjs/locale/en";
import "dayjs/locale/ko";
import "dayjs/locale/ja";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(localeData);

dayjs.extend(utc);
dayjs.extend(timezone);

export const transformDate = (timestamp: number | string | undefined, format?: string, locale?: string): string => {
	if (timestamp === undefined) {
		return "-";
	}

	return dayjs(timestamp)
		.locale(transformLocale(locale))
		.format(format || "YYYY-MM-DD");
};

export function transformLocale(locale?: string): string {
	if (!locale) return "en";
	switch (locale) {
		case "zh-Hans":
			return "zh-cn";
		case "zh-Hant":
			return "zh-tw";
		default:
			return locale;
	}
}

export function getWeekdays(locale: string) {
	dayjs.locale(transformLocale(locale));
	return dayjs.weekdaysShort();
}

/**
 * 10-12 | 12/18
 */
export function getMonthAndDay(timestamp: string | number, locale: string) {
	if (!timestamp) return "";
	const format = ["zh-Hans", "zh-Hant"].includes(locale) ? "MM-DD" : "MM/DD";
	return dayjs(timestamp).locale(transformLocale(locale)).format(format);
}

export function getWeek(timestamp: string | number, locale: string) {
	return dayjs(timestamp).locale(transformLocale(locale)).format("dddd");
}

export function isSameDayNewYork(timestamp: string | number) {
	return dayjs(timestamp).isSame(dayjs().tz("America/New_York"), "day");
}

// 根据美东时间判断是否大于 timestamp 也是美东时间
export function isAfterNewYork(timestamp: string | number) {
	return dayjs().tz("America/New_York").isAfter(dayjs(timestamp).tz("America/New_York"));
}

// 展示为美东时间
export function transformNewYorkTime(timestamp: string | number, format: string) {
	return dayjs(timestamp).tz("America/New_York").format(format);
}

// 香港时间
export function isAfterHongKongTime(timestamp: string | number) {
	dayjs.extend(utc);
	dayjs.extend(timezone);
	const now = dayjs().tz("Asia/Hong_Kong");
	const timestampInHK = dayjs(timestamp).tz("Asia/Hong_Kong");
	return now.isAfter(timestampInHK);
}

export function isAfterHongKongTimeConfig(timestamp: string | number) {
	const now = dayjs().tz("Asia/Hong_Kong");
	const timestampInHK = dayjs(timestamp);
	return {
		nowServer: dayjs().format("YYYY-MM-DD HH:mm:ss"),
		nowHK: now.format("YYYY-MM-DD HH:mm:ss"),
		timestampHK: timestampInHK.format("YYYY-MM-DD HH:mm:ss"),
		isAfter: now.isAfter(timestampInHK),
	};
}

// 北京时间
export function transformBeijingTime(timestamp: string | number, format: string, locale?: string) {
	dayjs.locale(transformLocale(locale));
	return dayjs(timestamp).tz("Asia/Shanghai").format(format);
}

/**
 * 时间戳转换为本地化格式
 * @param timestamp 时间戳（毫秒）
 * @param locale 语言环境
 * @returns 格式化的时间字符串
 */
export interface LocalTimestampParts {
	/** 2025年9月25日 | Sep 25, 2025 | 25 sep 2025 */
	date: string;
	/** 下午2:00 | 2:00 PM | 14:00 */
	time: string;
}

/**
 * 把时间拆成「日期」和「时刻」两段，供详情页时间轴左右分栏使用。
 *
 * 注意：是分别 format，不是去 split 拼好的字符串。按 " at " / " a las " 切分很脆——
 * 中文压根没有分隔词，西语是两个词，这类 bug 只会在小语种上线后才暴露。
 */
export function formatLocalTimestampParts(timestamp: number | string, locale = "en"): LocalTimestampParts {
	const date = dayjs(timestamp);

	switch (locale) {
		case "zh-Hans":
			return { date: date.locale("zh-cn").format("YYYY年M月D日"), time: date.locale("zh-cn").format("A h:mm") };

		case "zh-Hant":
			return { date: date.locale("zh-tw").format("YYYY年M月D日"), time: date.locale("zh-tw").format("A h:mm") };

		case "es":
			return { date: date.locale("es").format("D MMM YYYY"), time: date.locale("es").format("HH:mm") };

		case "en":
		default:
			return { date: date.locale("en").format("MMM DD, YYYY"), time: date.locale("en").format("h:mm A") };
	}
}

/** 拼装 formatLocalTimestampParts 的两段，使两者永不漂移 */
const LOCAL_TIMESTAMP_JOINER: Record<string, string> = {
	"zh-Hans": " ",
	"zh-Hant": " ",
	es: " a las ",
	en: " at ",
};

export function formatLocalTimestamp(timestamp: number | string, locale = "en"): string {
	// 2025年9月25日 下午2:00 | Sep 25, 2025 at 2:00 PM | 25 sep 2025 a las 14:00
	const { date, time } = formatLocalTimestampParts(timestamp, locale);
	return `${date}${LOCAL_TIMESTAMP_JOINER[locale] ?? LOCAL_TIMESTAMP_JOINER.en}${time}`;
}

// 获取一年的月份
export function getYearMonths(locale: string): string[] {
	dayjs.locale(transformLocale(locale));
	const months: string[] = [];
	for (let month = 0; month < 12; month++) {
		months.push(dayjs().month(month).format("MMM"));
	}
	return months;
}
